import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PhoneInput } from '@/components/ui/phone-input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useApi } from '@/hooks/useApi';
import { Loader2 } from 'lucide-react';

interface AddUserModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface CarrierOption {
  id: string;
  name: string;
}

interface TenantOption {
  id: string;
  name: string;
}

type UserType = 'carrier' | 'tenant';

export function AddUserModal({ open, onClose, onSuccess }: AddUserModalProps) {
  const { loading, post, get } = useApi({ showSuccessToast: true, showErrorToast: true });

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    phone: '',
    userType: '' as UserType | '',
    carrier_id: '',
    tenant_id: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [carriers, setCarriers] = useState<CarrierOption[]>([]);
  const [tenants, setTenants] = useState<TenantOption[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  useEffect(() => {
    if (open) {
      fetchOptions();
    }
  }, [open]);

  const fetchOptions = async () => {
    setLoadingOptions(true);
    try {
      const [carriersRes, tenantsRes] = await Promise.all([
        get('/api/v1/carriers', { limit: 100, is_active: true }).catch(() => null),
        get('/api/v1/tenants', { limit: 100 }).catch(() => null),
      ]);

      if (carriersRes?.list) {
        setCarriers(carriersRes.list.map((c: any) => ({ id: c.id, name: c.name })));
      }
      if (tenantsRes?.list) {
        setTenants(tenantsRes.list.map((t: any) => ({
          id: t.id,
          name: t.name_translations?.ar || t.name_translations?.en || t.name || t.id,
        })));
      }
    } catch {
      // ignore
    } finally {
      setLoadingOptions(false);
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'الاسم مطلوب';
    }

    if (!formData.email) {
      newErrors.email = 'البريد الإلكتروني مطلوب';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'البريد الإلكتروني غير صالح';
    }

    if (!formData.phone) {
      newErrors.phone = 'رقم الهاتف مطلوب';
    } else if (!/^\+\d{10,15}$/.test(formData.phone)) {
      newErrors.phone = 'رقم الهاتف يجب أن يبدأ بـ + ويحتوي على 10-15 رقم';
    }

    if (!formData.password) {
      newErrors.password = 'كلمة المرور مطلوبة';
    } else if (formData.password.length < 6) {
      newErrors.password = 'كلمة المرور يجب أن تكون 6 أحرف على الأقل';
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'كلمات المرور غير متطابقة';
    }

    if (!formData.userType) {
      newErrors.userType = 'يجب اختيار نوع المستخدم';
    }

    if (formData.userType === 'carrier' && !formData.carrier_id) {
      newErrors.carrier_id = 'يجب اختيار الناقل';
    }

    if (formData.userType === 'tenant' && !formData.tenant_id) {
      newErrors.tenant_id = 'يجب اختيار العميل';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    const submitData: Record<string, any> = {
      email: formData.email,
      password: formData.password,
      name: formData.name,
      phone: formData.phone,
    };

    if (formData.userType === 'carrier') {
      submitData.carrier_id = formData.carrier_id;
    } else if (formData.userType === 'tenant') {
      submitData.tenant_id = formData.tenant_id;
    }

    const result = await post('/api/v1/dashboard/create-user', submitData);

    if (result) {
      onSuccess();
      onClose();
      resetForm();
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      confirmPassword: '',
      name: '',
      phone: '',
      userType: '',
      carrier_id: '',
      tenant_id: '',
    });
    setErrors({});
  };

  const handleClose = () => {
    onClose();
    resetForm();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>إضافة مستخدم جديد</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">الاسم *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="الاسم الكامل"
              className={errors.name ? 'border-destructive' : ''}
            />
            {errors.name && <p className="text-xs text-destructive">{errors.name}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">البريد الإلكتروني *</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              placeholder="user@example.com"
              className={errors.email ? 'border-destructive' : ''}
            />
            {errors.email && <p className="text-xs text-destructive">{errors.email}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone">رقم الهاتف *</Label>
            <PhoneInput
              value={formData.phone}
              onChange={(val) => setFormData(prev => ({ ...prev, phone: val }))}
              error={errors.phone}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">كلمة المرور *</Label>
            <Input
              id="password"
              type="password"
              value={formData.password}
              onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
              placeholder="أدخل كلمة المرور"
              className={errors.password ? 'border-destructive' : ''}
            />
            {errors.password && <p className="text-xs text-destructive">{errors.password}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPassword">تأكيد كلمة المرور *</Label>
            <Input
              id="confirmPassword"
              type="password"
              value={formData.confirmPassword}
              onChange={(e) => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
              placeholder="أعد إدخال كلمة المرور"
              className={errors.confirmPassword ? 'border-destructive' : ''}
            />
            {errors.confirmPassword && <p className="text-xs text-destructive">{errors.confirmPassword}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="userType">نوع المستخدم *</Label>
            <Select
              value={formData.userType}
              onValueChange={(value) => setFormData(prev => ({
                ...prev,
                userType: value as UserType,
                carrier_id: '',
                tenant_id: '',
              }))}
            >
              <SelectTrigger className={errors.userType ? 'border-destructive' : ''}>
                <SelectValue placeholder="اختر نوع المستخدم" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="carrier">ناقل</SelectItem>
                <SelectItem value="tenant">عميل</SelectItem>
              </SelectContent>
            </Select>
            {errors.userType && <p className="text-xs text-destructive">{errors.userType}</p>}
          </div>

          {formData.userType === 'carrier' && (
            <div className="space-y-2">
              <Label htmlFor="carrier_id">الناقل *</Label>
              <Select
                value={formData.carrier_id}
                onValueChange={(value) => setFormData(prev => ({ ...prev, carrier_id: value }))}
              >
                <SelectTrigger className={errors.carrier_id ? 'border-destructive' : ''}>
                  <SelectValue placeholder={loadingOptions ? 'جاري التحميل...' : 'اختر الناقل'} />
                </SelectTrigger>
                <SelectContent>
                  {carriers.map((carrier) => (
                    <SelectItem key={carrier.id} value={carrier.id}>{carrier.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.carrier_id && <p className="text-xs text-destructive">{errors.carrier_id}</p>}
            </div>
          )}

          {formData.userType === 'tenant' && (
            <div className="space-y-2">
              <Label htmlFor="tenant_id">العميل *</Label>
              <Select
                value={formData.tenant_id}
                onValueChange={(value) => setFormData(prev => ({ ...prev, tenant_id: value }))}
              >
                <SelectTrigger className={errors.tenant_id ? 'border-destructive' : ''}>
                  <SelectValue placeholder={loadingOptions ? 'جاري التحميل...' : 'اختر العميل'} />
                </SelectTrigger>
                <SelectContent>
                  {tenants.map((tenant) => (
                    <SelectItem key={tenant.id} value={tenant.id}>{tenant.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.tenant_id && <p className="text-xs text-destructive">{errors.tenant_id}</p>}
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={handleClose}>
              إلغاء
            </Button>
            <Button type="submit" disabled={loading}>
              {loading && <Loader2 className="h-4 w-4 ms-2 animate-spin" />}
              إضافة المستخدم
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
